import { logger } from "../../utils/logger";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { Upload, Trash2, Edit2, Image, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GalleryManager() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [caption, setCaption] = useState("");
  const fileInputRef = useRef(null);
  const { getAuthHeaders } = useAuth();

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setImages(response.data);
    } catch (err) {
      logger.error("Error fetching gallery:", err);
      toast.error("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGallery();
  }, []);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const uploadPromises = [];

    for (let i = 0; i < files.length; i++) {
      const formData = new FormData();
      formData.append("file", files[i]);
      formData.append("caption", "");

      const token = sessionStorage.getItem("smsc_token");
      uploadPromises.push(
        axios.post(`${API}/gallery/upload`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        })
      );
    }

    try {
      await Promise.all(uploadPromises);
      toast.success(`${files.length} image(s) uploaded!`);
      fetchGallery();
    } catch (err) {
      toast.error("Failed to upload some images");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const openEditDialog = (image) => {
    setEditingImage(image);
    setCaption(image.caption || "");
    setEditDialogOpen(true);
  };

  const handleUpdateCaption = async () => {
    if (!editingImage) return;

    try {
      const formData = new FormData();
      formData.append("caption", caption);

      const token = sessionStorage.getItem("smsc_token");
      await axios.put(`${API}/gallery/${editingImage.id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success("Caption updated!");
      setEditDialogOpen(false);
      fetchGallery();
    } catch (err) {
      toast.error("Failed to update caption");
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;

    try {
      await axios.delete(`${API}/gallery/${imageId}`, getAuthHeaders());
      toast.success("Image deleted!");
      fetchGallery();
    } catch (err) {
      toast.error("Failed to delete image");
    }
  };

  if (loading) {
    return <div className="text-gold animate-pulse">Loading gallery...</div>;
  }

  return (
    <div data-testid="gallery-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl text-white mb-2">Gallery Manager</h1>
          <p className="text-white/60">Upload and manage your photo gallery</p>
        </div>
        <div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleUpload}
            accept="image/*"
            multiple
            className="hidden"
            data-testid="gallery-file-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-gold text-black hover:bg-gold-dark"
            data-testid="upload-images-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? "Uploading..." : "Upload Images"}
          </Button>
        </div>
      </div>

      {/* Gallery Grid */}
      {images.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Image className="w-12 h-12 text-gold/30 mx-auto mb-4" />
          <p className="text-white/60">No images yet. Upload your first photo!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image) => (
            <div
              key={image.id}
              className="relative group aspect-square rounded-xl overflow-hidden border border-white/10"
              data-testid={`gallery-item-${image.id}`}
            >
              <img
                src={image.url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${image.url}` : image.url}
                alt={image.caption || "Gallery image"}
                className="w-full h-full object-cover"
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/70 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                <Button
                  size="sm"
                  onClick={() => openEditDialog(image)}
                  className="bg-white/20 hover:bg-white/30 text-white"
                  data-testid={`edit-image-${image.id}`}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleDelete(image.id)}
                  className="bg-red-500/80 hover:bg-red-500 text-white"
                  data-testid={`delete-image-${image.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Caption */}
              {image.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                  <p className="text-white text-sm truncate">{image.caption}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-casino-dark border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit Caption</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {editingImage && (
              <img
                src={editingImage.url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${editingImage.url}` : editingImage.url}
                alt="Preview"
                className="w-full h-48 object-cover rounded-lg"
              />
            )}
            <div>
              <Label className="text-white/80">Caption</Label>
              <Input
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="Enter a caption for this image"
                data-testid="caption-input"
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
                className="flex-1 border-white/20 text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateCaption}
                className="flex-1 bg-gold text-black hover:bg-gold-dark"
                data-testid="save-caption-btn"
              >
                Save Caption
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
